import React from 'react';
import { useSWRConfig } from 'swr';
import {
  OWN_AWAITING_ACCEPTANCE,
  OWN_IN_CONSTRUCTION,
  OWN_SEND_ESTIMATE,
  ownStatusMovingValidation
} from '~/models/Status';
import Styles from './statuses.module.scss';
import useItandiAccount from '~/hooks/useItandiAccount';
import { ReportImageNavigation } from '~/components/common/reports/navigation/ReportImageNavigation';
import { dealCacheKey } from '~/hooks/useDeals';
import { update as updateDealConstructionSchedule } from '~/utils/api/deals/DealConstructionSchedule';
import { useDealCompletionReportImages } from '~/hooks/deals/useDealCompletionReportImages';
import { PATH as COMPLETE_REPORT_PATH } from '~/pages/deals/[id]/completion_reports';
import { OwnStatusHandleButtons } from '~/components/common/statuses/OwnStatusHandleButtons';
import { ScheduleInputNavigation } from '~/components/common/construction_schedule/ScheduleInputNavigation';
import type { DealConstructionScheduleEdit } from '~/models/DealConstructionSchedule';
import type { NskResponse } from '~/models/Api';
import { UploadFileNavigation } from '~/components/common/upload_files/UploadFileNavigation';
import {
  destroy as destroyReceiptFile,
  getFilePath
} from '~/utils/api/deals/DealUploadedFile';
import {
  getDownloadPath,
  create as uploadReceiptFile
} from '~/utils/api/deals/DealReceiptFile';
import type { DealUploadFile } from '~/models/DealUploadFile';
import { useDealContext } from '~/context/DealContext';

export const OwnInConstruction: React.FC = () => {
  const { data: account } = useItandiAccount().currentAccount();
  const deal = useDealContext();
  const { mutate: globalMutate } = useSWRConfig();
  const { data: completeImages } = useDealCompletionReportImages().index(
    deal.id
  );
  const statusDisabled = React.useMemo(() => {
    if (account === null) return true;
    return !ownStatusMovingValidation[OWN_IN_CONSTRUCTION](account);
  }, [account]);
  const handleMutateDeal = async (): Promise<void> => {
    await globalMutate(dealCacheKey(deal.id));
  };
  const handleSubmitDealConstructionSchedule = async (
    schedules: DealConstructionScheduleEdit[]
  ): Promise<NskResponse<void>> =>
    updateDealConstructionSchedule(deal.id, schedules);
  const handleDestroyReceipt = async (
    file: DealUploadFile
  ): Promise<NskResponse<void>> => destroyReceiptFile(deal.id, file.id);
  const handleUploadReceipt = async (file: File): Promise<NskResponse<void>> =>
    uploadReceiptFile(file, deal.id);
  const handleCreateReceipt = async (): Promise<NskResponse<void>> =>
    uploadReceiptFile(null, deal.id);
  const handleGetReceiptPath = (file: DealUploadFile): string =>
    getFilePath(deal.id, file);
  const downloadExcel = (): void => {
    const path = getDownloadPath(deal.id);
    window.open(path);
  };

  if (completeImages === null) return null;

  return (
    <div>
      <h3 className={Styles.Title}>工事中です</h3>
      <p className={Styles.Description}>
        工事完了後にステータスを進めてください
      </p>
      <UploadFileNavigation
        deal={deal}
        downloadExcel={downloadExcel}
        files={deal.dealReceiptFiles}
        fileType="工事発注書・請書"
        handleCreate={handleCreateReceipt}
        handleDestroy={handleDestroyReceipt}
        handleGetFilePath={handleGetReceiptPath}
        handleMutate={handleMutateDeal}
        handleUpload={handleUploadReceipt}
        title="工事発注書・請書を登録してください"
      />
      <ScheduleInputNavigation
        deal={deal}
        handleMutate={handleMutateDeal}
        handleSubmit={handleSubmitDealConstructionSchedule}
      />
      <ReportImageNavigation
        detailPageButtonLabel="画像登録画面"
        detailPagePath={COMPLETE_REPORT_PATH(deal.id)}
        optionalLabel
        reportImages={completeImages}
        title="完了報告画像を登録してください"
      />
      <OwnStatusHandleButtons
        backwardDisabled={statusDisabled}
        forwardDisabled={statusDisabled}
        nextStatusId={OWN_AWAITING_ACCEPTANCE}
        prevStatusId={OWN_SEND_ESTIMATE}
      />
    </div>
  );
};
